import React, {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import {
	readStoredPromptsOfTheDay,
	usePromptOfTheDayWithFallback,
} from '@atlassian/rovo-prompt-of-the-day/use-prompt-of-the-day-with-fallback';

import type { PromptOfTheDay } from '../../../common/utils/quick-find/prompt-of-the-day/types';
import { useUser } from '../../store/bootstrap';
import {
	usePromptOfTheDayLoadAnalytics,
	useReportPromptOfTheDayGenerated,
} from './use-prompt-of-the-day-load-analytics';

/** Quick Find regenerates 24 hours after the prompts were generated, not at local midnight. */
export const PROMPT_OF_THE_DAY_MAX_AGE_MS: number = 24 * 60 * 60 * 1000;

type RemovePrompt = (prompt: PromptOfTheDay) => void;

const PromptOfTheDayContext = createContext<PromptOfTheDay | undefined>(undefined);
const RemovePromptOfTheDayContext = createContext<RemovePrompt>(() => {});

/** The prompt to show as the first Quick Find row; `undefined` when there is none or no provider. */
export const usePromptOfTheDay: () => PromptOfTheDay | undefined = () =>
	useContext(PromptOfTheDayContext);

/** Removes a used prompt from the cache, so the next open shows the next one, or none. */
export const useRemovePromptOfTheDay: () => RemovePrompt = () =>
	useContext(RemovePromptOfTheDayContext);

type LoaderProps = {
	accountId: string;
	onRemovePrompt: (removePrompt: RemovePrompt | undefined) => void;
	onPrompts: (prompts: readonly PromptOfTheDay[]) => void;
};

// Mounted once the user engages. Starts the requests (and regeneration) and reports their outcome;
// the shown prompt is read on open (see the provider), from storage or these prompts.
const PromptOfTheDayLoader = ({ accountId, onRemovePrompt, onPrompts }: LoaderProps): null => {
	const onGenerated = useReportPromptOfTheDayGenerated();
	const { prompts, status, removePrompt } = usePromptOfTheDayWithFallback(
		{ accountId },
		{ maxAgeMs: PROMPT_OF_THE_DAY_MAX_AGE_MS, onGenerated },
	);
	usePromptOfTheDayLoadAnalytics({ status, promptCount: prompts.length });

	useEffect(() => {
		onRemovePrompt(removePrompt);
	}, [onRemovePrompt, removePrompt]);

	useEffect(() => {
		onPrompts(prompts);
	}, [onPrompts, prompts]);

	return null;
};

type ProviderProps = PropsWithChildren<{
	/** Delay the request until the user has engaged with Quick Find. */
	isInteracted?: boolean;
	/** While open, the displayed prompt is frozen so the list never shifts under the user. */
	isOpen?: boolean;
}>;

// Mount only when the feature is on (see isPromptOfTheDayEnabled).
export const QuickFindPromptOfTheDayProvider: (props: ProviderProps) => React.JSX.Element = ({
	isInteracted = false,
	isOpen = false,
	children,
}) => {
	const [user] = useUser();
	const accountId = user?.id;

	const [hasStarted, setHasStarted] = useState(isInteracted);
	if (isInteracted && !hasStarted) {
		setHasStarted(true);
	}
	// Read from the shared cache as Quick Find opens, then frozen until the next open: prompts used
	// in another surface are gone, regenerated ones are in, and the open list never shifts.
	// A layout effect, so storage is read off the server and before paint (no row popping in late).
	const [displayed, setDisplayed] = useState<PromptOfTheDay | undefined>(undefined);
	// The loader's latest prompts, for when storage holds nothing (blocked, or not written yet).
	const loadedPromptsRef = useRef<readonly PromptOfTheDay[]>([]);
	const onPrompts = useCallback((prompts: readonly PromptOfTheDay[]) => {
		loadedPromptsRef.current = prompts;
	}, []);
	// Starts closed, so a provider that first renders already open still reads on that open.
	const wasOpenRef = useRef(false);
	useLayoutEffect(() => {
		const hasJustOpened = isOpen && !wasOpenRef.current;
		wasOpenRef.current = isOpen;
		if (hasJustOpened) {
			// An empty stored list means every prompt was used, maybe elsewhere: no fallback then.
			const stored = accountId ? readStoredPromptsOfTheDay({ accountId }) : null;
			setDisplayed((stored ?? loadedPromptsRef.current)[0]);
		}
	}, [isOpen, accountId]);

	// A ref, so the context value stays stable while the loader mounts, remounts or re-renders.
	const removePromptRef = useRef<RemovePrompt | undefined>(undefined);
	const onRemovePrompt = useCallback((removePrompt: RemovePrompt | undefined) => {
		removePromptRef.current = removePrompt;
	}, []);
	const removePrompt = useCallback<RemovePrompt>((prompt) => {
		removePromptRef.current?.(prompt);
	}, []);

	return (
		<PromptOfTheDayContext.Provider value={displayed}>
			<RemovePromptOfTheDayContext.Provider value={removePrompt}>
				{hasStarted && accountId && (
					<PromptOfTheDayLoader
						accountId={accountId}
						onRemovePrompt={onRemovePrompt}
						onPrompts={onPrompts}
					/>
				)}
				{children}
			</RemovePromptOfTheDayContext.Provider>
		</PromptOfTheDayContext.Provider>
	);
};
